import React from 'react';
import { useDashboard } from '../../contexts/DashboardContext';
import { DayPicker } from 'react-day-picker';
import TagSelector from '../TagSelector';
import CouponApply from '../CouponApply';
import ActivityLog from '../ActivityLog';
import StripePaymentSection from '../payments/StripePaymentSection';

const AppointmentFormModal = () => {
  const [showActivity, setShowActivity] = React.useState(false);
  const {
    addressSuggestions, appointmentClientSearchQuery, appointmentClientSearchResults,
    appointmentForm, availableDates, availableTags, availableTimeSlots, calculateAllProductPricing,
    calculateAppointmentPricing, calculatedPricing, calendarMonth, checkingAvailability, clients,
    createNewClientInAppointment, editingAppointment, fetchMonthAvailability,
    fetchPhotographerAvailability, fetchPhotographerDayAppointments, fetchQuoteAvailability,
    handleAppointmentSubmit, isReschedulingAppointment, loadingAvailableDates,
    newClientInAppointmentForm, photographerDayAppointments, loadingDayAppointments,
    productPricing, productSearchQuery, products, packages, addonProducts,
    quoteAvailableSlots, loadingQuoteSlots, quoteSent, searchAddressSuggestions,
    searchAppointmentClients, selectAddressSuggestion, selectAppointmentClient, selectedProducts,
    selectedQuoteSlots, sendAppointmentQuote, sendingQuote, setAppointmentClientSearchQuery,
    setAppointmentForm, setAvailableDates, setAvailableTimeSlots, setCalculatedPricing,
    setCalendarMonth, setEditingAppointment, setIsReschedulingAppointment,
    setNewClientInAppointmentForm, setProductSearchQuery, setSelectedProducts,
    setSelectedQuoteSlots, setShowAppointmentClientSearch, setShowAppointmentForm,
    setShowCreateClientInAppointment, setShowProductDropdown, setShowQuoteBuilder,
    setQuoteSent, setTimeOverrideEnabled, showAddressSuggestions, showAppointmentClientSearch,
    showAppointmentForm, showCreateClientInAppointment, showProductDropdown, showQuoteBuilder,
    showToast, teamMembers, timeOverrideEnabled, toggleQuoteSlot, resetAppointmentForm,
    addressSearchHighlightIndex, clientSearchHighlightIndex, productSearchHighlightIndex,
    selectedPhotographerAvailability, setSelectedPhotographerAvailability,
  } = useDashboard();

  // Destructure setters separately so the main hook block above stays alphabetical.
  const {
    setClientSearchHighlightIndex, setAddressSearchHighlightIndex,
  } = useDashboard();

  if (!showAppointmentForm) return null;

  return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[95vw] md:max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}
                </h3>
                <div className="flex items-center gap-2">
                  {editingAppointment && (
                    <button
                      type="button"
                      onClick={() => setShowActivity(true)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-1.5"
                      data-testid="view-activity-btn"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      View Activity
                    </button>
                  )}
                  {!editingAppointment && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!showQuoteBuilder) {
                          setShowQuoteBuilder(true);
                          if (calculatedPricing?.total_duration && selectedProducts.length > 0) {
                            fetchQuoteAvailability();
                          }
                        } else {
                          setShowQuoteBuilder(false);
                          setSelectedQuoteSlots([]);
                          setQuoteSent(false);
                        }
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        showQuoteBuilder
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                      data-testid="build-quote-btn"
                    >
                      {showQuoteBuilder ? 'Close Quote Builder' : 'Build Appointment Quote'}
                    </button>
                  )}
                </div>
              </div>
              <form onSubmit={handleAppointmentSubmit} className="p-6">
                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-4">
                    {/* Client Search */}
                    <div className="relative client-search-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Client *</label>
                      {!showCreateClientInAppointment ? (
                        <>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              required
                              value={appointmentClientSearchQuery}
                              onChange={(e) => {
                                setAppointmentClientSearchQuery(e.target.value);
                                searchAppointmentClients(e.target.value);
                                setClientSearchHighlightIndex(-1);
                              }}
                              onFocus={() => {
                                if (appointmentClientSearchResults.length > 0) {
                                  setShowAppointmentClientSearch(true);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (!showAppointmentClientSearch || appointmentClientSearchResults.length === 0) return;
                                const max = appointmentClientSearchResults.length - 1;
                                if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  setClientSearchHighlightIndex((prev) => (prev >= max ? 0 : prev + 1));
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  setClientSearchHighlightIndex((prev) => (prev <= 0 ? max : prev - 1));
                                } else if (e.key === 'Enter') {
                                  const idx = clientSearchHighlightIndex >= 0 ? clientSearchHighlightIndex : 0;
                                  const target = appointmentClientSearchResults[idx];
                                  if (target) {
                                    e.preventDefault();
                                    selectAppointmentClient(target);
                                  }
                                } else if (e.key === 'Escape') {
                                  setShowAppointmentClientSearch(false);
                                }
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Search by name, email, or company..."
                              autoComplete="off"
                              data-testid="appointment-client-search-input"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateClientInAppointment(true);
                                setShowAppointmentClientSearch(false);
                              }}
                              className="px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                            >
                              + New
                            </button>
                          </div>
                          {showAppointmentClientSearch && appointmentClientSearchResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                              {appointmentClientSearchResults.map((client, idx) => (
                                <div
                                  key={client.id}
                                  onMouseEnter={() => setClientSearchHighlightIndex(idx)}
                                  onClick={() => selectAppointmentClient(client)}
                                  className={`p-3 cursor-pointer border-b last:border-b-0 ${idx === clientSearchHighlightIndex ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                                  data-testid={`appointment-client-option-${client.id}`}
                                >
                                  <p className="font-medium text-gray-900">{client.name}</p>
                                  {client.company && <p className="text-xs text-gray-500">{client.company}</p>}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="bg-gray-50 p-4 rounded-lg border">
                          <div className="space-y-3">
                            <input type="text" placeholder="Name *" value={newClientInAppointmentForm.name} onChange={(e) => setNewClientInAppointmentForm({...newClientInAppointmentForm, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" required />
                            <input type="email" placeholder="Email" value={newClientInAppointmentForm.email} onChange={(e) => setNewClientInAppointmentForm({...newClientInAppointmentForm, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
                            <input type="tel" placeholder="Phone" value={newClientInAppointmentForm.phone} onChange={(e) => setNewClientInAppointmentForm({...newClientInAppointmentForm, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
                            <div className="flex gap-2">
                              <button type="button" onClick={createNewClientInAppointment} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm">Create & Select</button>
                              <button type="button" onClick={() => setShowCreateClientInAppointment(false)} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Property Address */}
                    <div className="relative address-search-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Property Address *</label>
                      <input
                        type="text"
                        required
                        value={appointmentForm.property_address}
                        onChange={(e) => {
                          setAppointmentForm({...appointmentForm, property_address: e.target.value});
                          searchAddressSuggestions(e.target.value);
                          setAddressSearchHighlightIndex(-1);
                        }}
                        onKeyDown={(e) => {
                          if (!showAddressSuggestions || addressSuggestions.length === 0) return;
                          const max = addressSuggestions.length - 1;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setAddressSearchHighlightIndex((prev) => (prev >= max ? 0 : prev + 1));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setAddressSearchHighlightIndex((prev) => (prev <= 0 ? max : prev - 1));
                          } else if (e.key === 'Enter') {
                            const idx = addressSearchHighlightIndex >= 0 ? addressSearchHighlightIndex : 0;
                            const target = addressSuggestions[idx];
                            if (target) {
                              e.preventDefault();
                              selectAddressSuggestion(target);
                            }
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Start typing address..."
                        autoComplete="off"
                        data-testid="appointment-property-address-input"
                      />
                      {showAddressSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {addressSuggestions.map((suggestion, idx) => (
                            <div
                              key={suggestion.place_id}
                              onMouseEnter={() => setAddressSearchHighlightIndex(idx)}
                              onClick={() => selectAddressSuggestion(suggestion)}
                              className={`p-3 cursor-pointer border-b last:border-b-0 ${idx === addressSearchHighlightIndex ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                              data-testid={`appointment-address-option-${idx}`}
                            >
                              <p className="text-sm font-medium">{suggestion.structured_formatting?.main_text || suggestion.description}</p>
                              <p className="text-xs text-gray-600">{suggestion.structured_formatting?.secondary_text || ''}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Property Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Property Size (sq ft) *</label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={appointmentForm.property_sqft}
                        onChange={(e) => {
                          setAppointmentForm({...appointmentForm, property_sqft: e.target.value});
                          if (e.target.value > 0) {
                            calculateAllProductPricing(e.target.value);
                            if (selectedProducts.length > 0) {
                              calculateAppointmentPricing(selectedProducts, e.target.value);
                            }
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 2500"
                      />
                    </div>

                    {/* Notes - moved above Products */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                      <textarea
                        value={appointmentForm.notes}
                        onChange={(e) => setAppointmentForm({...appointmentForm, notes: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="Add any special instructions..."
                      />
                    </div>

                    {/* Products Selection */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Products/Services *</label>
                      <input
                        type="text"
                        value={productSearchQuery}
                        onChange={(e) => {
                          setProductSearchQuery(e.target.value);
                          setShowProductDropdown(true);
                        }}
                        onFocus={() => setShowProductDropdown(true)}
                        placeholder={selectedProducts.length > 0 ? `${selectedProducts.length} selected - search more...` : "Search products, packages & add-ons..."}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        data-testid="appointment-product-search-input"
                      />
                      {showProductDropdown && (() => {
                        const q = (productSearchQuery || '').toLowerCase();
                        const productMatches = (products || []).filter(p =>
                          p.is_active !== false &&
                          !selectedProducts.includes(p.id) &&
                          (p.name || '').toLowerCase().includes(q)
                        );
                        const packageMatches = (packages || []).filter(p =>
                          p.is_active !== false &&
                          (p.name || '').toLowerCase().includes(q)
                        );
                        const addonMatches = (addonProducts || []).filter(a =>
                          a.is_active !== false &&
                          !selectedProducts.includes(a.id) &&
                          (a.name || '').toLowerCase().includes(q)
                        );
                        const empty = productMatches.length === 0 && packageMatches.length === 0 && addonMatches.length === 0;
                        return (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="appointment-product-dropdown">
                            {/* Products */}
                            {productMatches.length > 0 && (
                              <div>
                                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-50 border-b">Products</div>
                                {productMatches.map((product) => (
                                  <div
                                    key={`product-${product.id}`}
                                    onClick={() => {
                                      const updated = [...selectedProducts, product.id];
                                      setSelectedProducts(updated);
                                      setProductSearchQuery('');
                                      setShowProductDropdown(false);
                                      if (updated.length > 0 && appointmentForm.property_sqft) {
                                        calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                      }
                                    }}
                                    className="p-3 cursor-pointer hover:bg-blue-50 border-b last:border-b-0"
                                    data-testid={`appointment-product-option-${product.id}`}
                                  >
                                    <div className="flex justify-between">
                                      <span className="font-medium">{product.name}</span>
                                      <span className="text-green-600">${productPricing[product.id]?.calculated_price || product.price}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Packages — clicking expands to all included products */}
                            {packageMatches.length > 0 && (
                              <div>
                                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 border-b">Packages</div>
                                {packageMatches.map((pkg) => {
                                  const included = (pkg.product_ids || []).filter(id => !selectedProducts.includes(id));
                                  return (
                                    <div
                                      key={`pkg-${pkg.id}`}
                                      onClick={() => {
                                        if (included.length === 0) {
                                          setProductSearchQuery('');
                                          setShowProductDropdown(false);
                                          return;
                                        }
                                        const updated = [...selectedProducts, ...included];
                                        setSelectedProducts(updated);
                                        setProductSearchQuery('');
                                        setShowProductDropdown(false);
                                        if (appointmentForm.property_sqft) {
                                          calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                        }
                                      }}
                                      className="p-3 cursor-pointer hover:bg-violet-50 border-b last:border-b-0"
                                      data-testid={`appointment-package-option-${pkg.id}`}
                                    >
                                      <div className="flex justify-between items-start">
                                        <div className="min-w-0">
                                          <div className="font-medium">{pkg.name}</div>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {(pkg.product_ids || []).length} product{(pkg.product_ids || []).length === 1 ? '' : 's'} included
                                          </div>
                                        </div>
                                        <span className="text-violet-700 font-semibold ml-2">${pkg.price || 0}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {/* Add-on products */}
                            {addonMatches.length > 0 && (
                              <div>
                                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border-b">Add-ons</div>
                                {addonMatches.map((addon) => (
                                  <div
                                    key={`addon-${addon.id}`}
                                    onClick={() => {
                                      const updated = [...selectedProducts, addon.id];
                                      setSelectedProducts(updated);
                                      setProductSearchQuery('');
                                      setShowProductDropdown(false);
                                      if (appointmentForm.property_sqft) {
                                        calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                      }
                                    }}
                                    className="p-3 cursor-pointer hover:bg-amber-50 border-b last:border-b-0"
                                    data-testid={`appointment-addon-option-${addon.id}`}
                                  >
                                    <div className="flex justify-between">
                                      <span className="font-medium">{addon.name}</span>
                                      <span className="text-amber-700 font-semibold">${addon.base_price || addon.price || 0}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {empty && (
                              <div className="p-3 text-sm text-gray-500 italic">No matching products, packages, or add-ons</div>
                            )}
                            <button type="button" onClick={() => setShowProductDropdown(false)} className="w-full p-2 text-sm text-gray-500 hover:bg-gray-100 border-t">Close</button>
                          </div>
                        );
                      })()}

                      {/* Selected Services Summary — visible for both new and existing appointments */}
                      {selectedProducts.length > 0 && (
                        <div
                          data-testid="appointment-selected-services-summary"
                          className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
                              Selected services ({selectedProducts.length})
                            </span>
                            {calculatedPricing && (
                              <span className="text-xs text-indigo-700">
                                Total: <span className="font-bold">${calculatedPricing.total_price}</span>
                                {calculatedPricing.total_duration ? ` · ${calculatedPricing.total_duration} min` : ''}
                              </span>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            {selectedProducts.map((productId) => {
                              const product = products.find(p => p.id === productId);
                              const addon = !product ? (addonProducts || []).find(a => a.id === productId) : null;
                              const item = product || addon;
                              if (!item) return null;
                              const isAddon = !!addon && !product;
                              const livePrice = productPricing[productId]?.calculated_price ?? (item.base_price ?? item.price);
                              const liveDuration = productPricing[productId]?.duration_minutes ?? item.duration_minutes;
                              return (
                                <div
                                  key={productId}
                                  data-testid={`selected-service-row-${productId}`}
                                  className="flex items-center justify-between bg-white px-3 py-2 rounded border border-indigo-100"
                                >
                                  <span className="text-sm font-medium text-gray-900 truncate flex-1 mr-2">
                                    {item.name}
                                    {isAddon && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 uppercase tracking-wide">Add-on</span>}
                                  </span>
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-xs text-gray-500" data-testid={`selected-service-duration-${productId}`}>
                                      {liveDuration ? `${liveDuration} min` : '—'}
                                    </span>
                                    <span className="text-sm font-semibold text-indigo-700" data-testid={`selected-service-price-${productId}`}>
                                      ${livePrice}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = selectedProducts.filter(id => id !== productId);
                                        setSelectedProducts(updated);
                                        if (updated.length > 0 && appointmentForm.property_sqft) {
                                          calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                        } else {
                                          setCalculatedPricing(null);
                                        }
                                      }}
                                      className="text-gray-300 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-red-50"
                                      aria-label="Remove service"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                  {/* RIGHT COLUMN - Scheduling or Quote Builder */}
                  <div className="space-y-4">
                    {/* Quote Builder Mode */}
                    {showQuoteBuilder && !editingAppointment && (
                      <div className="space-y-4">
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-indigo-900 mb-1">Appointment Quote Builder</h4>
                          <p className="text-xs text-indigo-600">Select up to 3 available time slots to send to the client. They can choose their preferred time and complete the booking.</p>
                        </div>

                        {/* Requirements check: client + products + sqft must be set */}
                        {(!appointmentForm.client_id || !selectedProducts.length || !appointmentForm.property_sqft || !calculatedPricing) ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-center">
                            <svg className="mx-auto h-10 w-10 text-amber-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                            <h4 className="font-semibold text-amber-800 text-sm">Complete the form first</h4>
                            <p className="text-xs text-amber-600 mt-1">Select a client, products/services, and enter the property size on the left. Available time slots will load based on the calculated duration.</p>
                            <div className="mt-3 space-y-1 text-left max-w-xs mx-auto">
                              {[
                                { ok: !!appointmentForm.client_id, label: `Client selected${appointmentForm.client_id ? ` (${clients.find(c => c.id === appointmentForm.client_id)?.name || ''})` : ''}` },
                                { ok: selectedProducts.length > 0, label: `Products/Services selected (${selectedProducts.length})` },
                                { ok: !!appointmentForm.property_sqft, label: `Property size entered${appointmentForm.property_sqft ? ` (${appointmentForm.property_sqft} sq ft)` : ''}` },
                                { ok: !!calculatedPricing, label: `Duration calculated${calculatedPricing ? ` (${calculatedPricing.total_duration} min)` : ''}` }
                              ].map((item, idx) => (
                                <div key={idx} className={`flex items-center gap-2 text-xs ${item.ok ? 'text-green-600' : 'text-gray-400'}`}>
                                  {item.ok ? (
                                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                  ) : (
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2"/></svg>
                                  )}
                                  {item.label}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                        {/* Duration info banner */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
                          <div className="text-xs text-blue-800">
                            <span className="font-semibold">Duration:</span> {calculatedPricing.total_duration} min &middot; <span className="font-semibold">Price:</span> ${calculatedPricing.total_price?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                          </div>
                          <span className="text-xs text-blue-600">
                            Sending to: {clients.find(c => c.id === appointmentForm.client_id)?.email || 'No email'}
                          </span>
                        </div>

                        {quoteSent ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                            <svg className="mx-auto h-12 w-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h4 className="font-semibold text-green-800 text-lg">Quote Sent!</h4>
                            <p className="text-sm text-green-600 mt-1">The client will receive an email with the available time options.</p>
                            <button type="button" onClick={resetAppointmentForm} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">Done</button>
                          </div>
                        ) : (
                          <>
                            {/* Selected slots summary */}
                            {selectedQuoteSlots.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-gray-700">Selected ({selectedQuoteSlots.length}/3)</h4>
                                  <button type="button" onClick={() => setSelectedQuoteSlots([])} className="text-xs text-red-500 hover:text-red-700">Clear all</button>
                                </div>
                                <div className="space-y-2">
                                  {selectedQuoteSlots.map((slot, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-indigo-50 rounded-lg px-3 py-2">
                                      <div>
                                        <span className="text-sm font-medium text-indigo-900">{slot.date_display}</span>
                                        <span className="text-sm text-indigo-700 ml-2">{slot.time_12h}</span>
                                        <span className="text-xs text-indigo-500 ml-2">with {slot.photographer_name}</span>
                                      </div>
                                      <button type="button" onClick={() => setSelectedQuoteSlots(selectedQuoteSlots.filter((_, i) => i !== idx))} className="text-indigo-400 hover:text-red-500 ml-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={sendAppointmentQuote}
                                  disabled={sendingQuote || selectedQuoteSlots.length === 0}
                                  className="w-full mt-3 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                  data-testid="send-quote-btn"
                                >
                                  {sendingQuote ? (<><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Sending...</>) : (<>Send Quote Email</>)}
                                </button>
                              </div>
                            )}

                            {/* Photographer Selection for Quote */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Photographer</label>
                              <select
                                value={appointmentForm.photographer_id}
                                onChange={(e) => {
                                  setAppointmentForm({...appointmentForm, photographer_id: e.target.value, scheduled_time: ''});
                                  setAvailableDates({});
                                  if (e.target.value) {
                                    fetchMonthAvailability(e.target.value, calendarMonth);
                                    if (appointmentForm.scheduled_date) {
                                      fetchPhotographerAvailability(e.target.value, appointmentForm.scheduled_date, calculatedPricing?.total_duration || 120);
                                    }
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                data-testid="quote-photographer-select"
                              >
                                <option value="">Select photographer</option>
                                <option value="any">Any Available</option>
                                {teamMembers.filter(member => {
                                  if (selectedProducts.length === 0) return true;
                                  const assignedProducts = member.assigned_products || [];
                                  return selectedProducts.every(productId => assignedProducts.includes(productId));
                                }).map(member => (
                                  <option key={member.id} value={member.id}>{member.name} - {member.role}</option>
                                ))}
                              </select>
                            </div>

                            {/* Calendar for Quote */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Date
                                {checkingAvailability && (
                                  <span className="ml-2 inline-flex items-center text-blue-600">
                                    <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    <span className="text-xs">Loading...</span>
                                  </span>
                                )}
                              </label>
                              <div className="bg-white border border-gray-200 rounded-lg p-3 relative">
                                {checkingAvailability && (
                                  <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
                                    <div className="flex items-center gap-2 text-blue-600">
                                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                      <span className="text-sm font-medium">Checking availability...</span>
                                    </div>
                                  </div>
                                )}
                                <DayPicker
                                  mode="single"
                                  selected={appointmentForm.scheduled_date ? new Date(appointmentForm.scheduled_date + 'T12:00:00') : undefined}
                                  onSelect={(date) => {
                                    if (date) {
                                      const dateStr = date.toISOString().split('T')[0];
                                      setAppointmentForm({...appointmentForm, scheduled_date: dateStr, scheduled_time: ''});
                                      setAvailableTimeSlots([]);
                                      if (appointmentForm.photographer_id) {
                                        fetchPhotographerAvailability(appointmentForm.photographer_id, dateStr, calculatedPricing?.total_duration || 120);
                                      }
                                    }
                                  }}
                                  onMonthChange={(month) => {
                                    setCalendarMonth(month);
                                    setAvailableDates({});
                                    if (appointmentForm.photographer_id) {
                                      fetchMonthAvailability(appointmentForm.photographer_id, month);
                                    }
                                  }}
                                  month={calendarMonth}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    if (date < today) return true;
                                    const dateStr = date.toISOString().split('T')[0];
                                    if (availableDates[dateStr] === false) return true;
                                    return false;
                                  }}
                                  modifiers={{
                                    available: (date) => availableDates[date.toISOString().split('T')[0]] === true,
                                    unavailable: (date) => availableDates[date.toISOString().split('T')[0]] === false,
                                    selected: (date) => appointmentForm.scheduled_date && date.toISOString().split('T')[0] === appointmentForm.scheduled_date
                                  }}
                                  modifiersStyles={{
                                    available: { backgroundColor: '#dcfce7', color: '#166534', fontWeight: '500' },
                                    unavailable: { backgroundColor: '#fee2e2', color: '#991b1b', opacity: 0.4 },
                                    selected: { backgroundColor: '#2563eb', color: 'white', fontWeight: '600', borderRadius: '8px' }
                                  }}
                                  modifiersClassNames={{ selected: 'ring-2 ring-blue-400 ring-offset-1' }}
                                  styles={{
                                    caption: { color: '#1f2937', fontWeight: '600' },
                                    head_cell: { color: '#6b7280', fontSize: '0.75rem', fontWeight: '500' },
                                    day: { margin: '2px', borderRadius: '6px', transition: 'all 0.15s ease' },
                                    nav_button: { color: '#3b82f6' },
                                    nav_button_previous: { marginRight: '8px' },
                                    nav_button_next: { marginLeft: '8px' }
                                  }}
                                  className="!font-sans"
                                  fromDate={new Date()}
                                />
                                <div className="mt-3 pt-3 border-t space-y-2">
                                  {appointmentForm.scheduled_date && (
                                    <div className="bg-blue-50 px-3 py-2 rounded-lg flex items-center justify-between">
                                      <span className="text-sm font-medium text-blue-800">
                                        {new Date(appointmentForm.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                      </span>
                                      <button type="button" onClick={() => setAppointmentForm({...appointmentForm, scheduled_date: '', scheduled_time: ''})} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Clear</button>
                                    </div>
                                  )}
                                  {appointmentForm.photographer_id && (
                                    <div className="flex items-center gap-4 text-xs text-gray-500 px-1">
                                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span> Available</span>
                                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 opacity-50"></span> Unavailable</span>
                                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-600"></span> Selected</span>
                                    </div>
                                  )}
                                  {!appointmentForm.photographer_id && (
                                    <p className="text-xs text-amber-600 px-1">Select a photographer to see availability</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Time Slots for Quote - Multi-select */}
                            {appointmentForm.scheduled_date && (
                              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                                  <span>Available Times ({selectedQuoteSlots.length}/3 selected)</span>
                                  {checkingAvailability && (
                                    <span className="inline-flex items-center text-blue-600">
                                      <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                      <span className="text-xs">Loading...</span>
                                    </span>
                                  )}
                                </label>
                                <p className="text-xs text-gray-500 mb-3">Click times to add to quote. Pick from different dates for variety.</p>
                                {checkingAvailability ? (
                                  <div className="flex items-center justify-center py-8">
                                    <div className="flex flex-col items-center gap-2 text-gray-500">
                                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                      <span className="text-sm">Finding available times...</span>
                                    </div>
                                  </div>
                                ) : availableTimeSlots.length > 0 ? (
                                  <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                                    {availableTimeSlots.map((slot) => {
                                      const photographerName = appointmentForm.photographer_id === 'any'
                                        ? (teamMembers.find(m => m.id === slot.photographer_id)?.name || '')
                                        : (teamMembers.find(m => m.id === appointmentForm.photographer_id)?.name || '');
                                      const quoteSlot = {
                                        date: appointmentForm.scheduled_date,
                                        date_display: new Date(appointmentForm.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
                                        time: slot.time,
                                        time_12h: slot.time_12h,
                                        photographer_id: slot.photographer_id || appointmentForm.photographer_id,
                                        photographer_name: photographerName
                                      };
                                      const slotKey = `${quoteSlot.date}-${quoteSlot.time}-${quoteSlot.photographer_id}`;
                                      const isInQuote = selectedQuoteSlots.some(s => `${s.date}-${s.time}-${s.photographer_id}` === slotKey);
                                      return (
                                        <button
                                          key={slot.time}
                                          type="button"
                                          onClick={() => toggleQuoteSlot(quoteSlot)}
                                          className={`px-3 py-2.5 text-left rounded-lg border transition-all duration-150 flex items-center justify-between ${
                                            isInQuote
                                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                                              : 'bg-white text-gray-700 border-gray-300 hover:bg-indigo-50 hover:border-indigo-300'
                                          }`}
                                          data-testid={`quote-time-${slot.time}`}
                                        >
                                          <div>
                                            <span className="text-sm font-medium">{slot.time_12h}</span>
                                            <span className={`block text-xs ${isInQuote ? 'text-indigo-200' : 'text-gray-500'}`}>{photographerName}</span>
                                          </div>
                                          {isInQuote && (
                                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-center py-6 bg-white rounded-lg border border-dashed border-gray-300">
                                    <p className="text-sm text-gray-500">No available time slots for this date.</p>
                                    <p className="text-xs text-gray-400 mt-1">Try a different date.</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Normal Scheduling (when not in quote mode) */}
                    {!showQuoteBuilder && (
                      <>
                    {/* Locked Schedule Display (when editing) */}
                    {editingAppointment && !isReschedulingAppointment && (
                      <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Current Schedule</h4>
                          <button type="button" onClick={() => setIsReschedulingAppointment(true)} className="px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600">Reschedule</button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <p className="text-[10px] text-blue-600 uppercase tracking-wide">Photographer</p>
                            <p className="text-sm font-medium text-blue-900">{teamMembers.find(m => m.id === appointmentForm.photographer_id)?.name || 'Unassigned'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-blue-600 uppercase tracking-wide">Date</p>
                            <p className="text-sm font-medium text-blue-900">{appointmentForm.scheduled_date ? new Date(appointmentForm.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-blue-600 uppercase tracking-wide">Time</p>
                            <p className="text-sm font-medium text-blue-900">{appointmentForm.scheduled_time || '—'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Order Details - Shows when editing an existing appointment (moved to right column) */}
                    {editingAppointment && (
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200 shadow-sm">
                        <h4 className="text-base font-bold text-indigo-900 mb-3 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Order Details
                        </h4>
                        
                        {/* Order Number */}
                        <div className="mb-3 pb-3 border-b border-indigo-100">
                          <p className="text-xs text-indigo-600 font-medium">ORDER #{editingAppointment.id?.slice(-6).toUpperCase()}</p>
                          <p className="text-sm text-gray-600">
                            Property: {editingAppointment.property_sqft?.toLocaleString() || appointmentForm.property_sqft?.toLocaleString() || 'N/A'} sq ft
                          </p>
                        </div>
                        
                        {/* Products/Services List */}
                        <div className="space-y-2 mb-4">
                          <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">Services Ordered</p>
                          
                          {/* Show from stored product_pricing if available, otherwise calculate from selected_products */}
                          {(editingAppointment.product_pricing && editingAppointment.product_pricing.length > 0) ? (
                            editingAppointment.product_pricing.map((item, idx) => (
                              <div key={idx} className="bg-white px-3 py-2 rounded border border-indigo-100">
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-900 truncate">{item.product_name}</span>
                                    {item.duration && (
                                      <span className="text-xs text-gray-400 whitespace-nowrap">
                                        {Math.floor(item.duration / 60)}h{item.duration % 60 > 0 ? ` ${item.duration % 60}m` : ''}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 ml-2">
                                    <span className="text-sm font-bold text-indigo-700">${item.price?.toFixed(2) || '0.00'}</span>
                                    <button type="button" onClick={() => {
                                      const updated = selectedProducts.filter(id => id !== item.product_id);
                                      setSelectedProducts(updated);
                                      if (updated.length > 0 && appointmentForm.property_sqft) {
                                        calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                      } else {
                                        setCalculatedPricing(null);
                                      }
                                    }} className="text-gray-300 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-red-50">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : calculatedPricing?.products ? (
                            calculatedPricing.products.map((product, idx) => (
                              <div key={idx} className="bg-white px-3 py-2 rounded border border-indigo-100">
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-900 truncate">{product.product_name}</span>
                                    {product.calculated_duration && (
                                      <span className="text-xs text-gray-400 whitespace-nowrap">
                                        {Math.floor(product.calculated_duration / 60)}h{product.calculated_duration % 60 > 0 ? ` ${product.calculated_duration % 60}m` : ''}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 ml-2">
                                    <span className="text-sm font-bold text-indigo-700">${product.calculated_price}</span>
                                    <button type="button" onClick={() => {
                                      const updated = selectedProducts.filter(id => id !== product.product_id);
                                      setSelectedProducts(updated);
                                      if (updated.length > 0 && appointmentForm.property_sqft) {
                                        calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                      } else {
                                        setCalculatedPricing(null);
                                      }
                                    }} className="text-gray-300 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-red-50">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : selectedProducts.length > 0 ? (
                            selectedProducts.map((productId, idx) => {
                              const product = products.find(p => p.id === productId);
                              return product ? (
                                <div key={idx} className="bg-white px-3 py-2 rounded border border-indigo-100">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-900 truncate">{product.name}</span>
                                    <div className="flex items-center gap-1.5 ml-2">
                                      <span className="text-sm font-bold text-indigo-700">${product.price}</span>
                                      <button type="button" onClick={() => {
                                        const updated = selectedProducts.filter(id => id !== productId);
                                        setSelectedProducts(updated);
                                        if (updated.length > 0 && appointmentForm.property_sqft) {
                                          calculateAppointmentPricing(updated, appointmentForm.property_sqft);
                                        } else {
                                          setCalculatedPricing(null);
                                        }
                                      }} className="text-gray-300 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-red-50">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })
                          ) : (
                            <p className="text-sm text-gray-500 italic">No services selected</p>
                          )}
                        </div>
                        
                        {/* Coupon Code */}
                        <div className="border-t border-indigo-100 pt-3">
                          <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide mb-2">Coupon Code</p>
                          <CouponApply
                            appointmentId={editingAppointment.id}
                            clientId={editingAppointment.client_id}
                            orderTotal={editingAppointment.total_price || calculatedPricing?.total_price || 0}
                            appliedCoupon={editingAppointment.coupon_code ? {
                              code: editingAppointment.coupon_code,
                              discount: editingAppointment.coupon_discount || 0
                            } : null}
                            onCouponApplied={(couponData) => {
                              setEditingAppointment(prev => ({
                                ...prev,
                                coupon_code: couponData.code,
                                coupon_discount: couponData.discount,
                                total_price: couponData.new_total
                              }));
                            }}
                            onCouponRemoved={(restoredTotal) => {
                              setEditingAppointment(prev => ({
                                ...prev,
                                coupon_code: null,
                                coupon_discount: null,
                                total_price: restoredTotal
                              }));
                            }}
                            showToast={showToast}
                          />
                        </div>

                        {/* Tags */}
                        <div className="border-t border-indigo-100 pt-3">
                          <TagSelector
                            selectedTagIds={editingAppointment.tag_ids || []}
                            availableTags={availableTags}
                            onChange={async (newTagIds) => {
                              try {
                                await axios.put(`${API}/appointments/${editingAppointment.id}/tags`, { tag_ids: newTagIds });
                                setEditingAppointment(prev => ({ ...prev, tag_ids: newTagIds }));
                              } catch (err) {
                                showToast('Failed to update tags', 'error');
                              }
                            }}
                          />
                        </div>

                        {/* Totals */}
                        <div className="border-t border-indigo-200 pt-3 space-y-2">
                          {/* Duration */}
                          <div className="flex justify-between text-sm">
                            <span className="text-indigo-700">Total Duration:</span>
                            <span className="font-medium text-indigo-800">
                              {editingAppointment.total_duration ? (
                                `${Math.floor(editingAppointment.total_duration / 60)}h ${editingAppointment.total_duration % 60 > 0 ? `${editingAppointment.total_duration % 60}m` : ''}`
                              ) : calculatedPricing?.total_duration ? (
                                `${Math.floor(calculatedPricing.total_duration / 60)}h ${calculatedPricing.total_duration % 60 > 0 ? `${calculatedPricing.total_duration % 60}m` : ''}`
                              ) : (
                                `${Math.floor((editingAppointment.duration_minutes || 120) / 60)}h ${(editingAppointment.duration_minutes || 120) % 60 > 0 ? `${(editingAppointment.duration_minutes || 120) % 60}m` : ''}`
                              )}
                            </span>
                          </div>
                          
                          {/* Order Total */}
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-indigo-900">Order Total:</span>
                            <span className="text-xl font-bold text-indigo-700">
                              ${(editingAppointment.total_price > 0 
                                ? editingAppointment.total_price.toFixed(2) 
                                : calculatedPricing?.total_price 
                                  ? calculatedPricing.total_price.toFixed(2)
                                  : editingAppointment.product_pricing?.length > 0
                                    ? editingAppointment.product_pricing.reduce((sum, p) => sum + (p.price || 0), 0).toFixed(2)
                                    : selectedProducts.length > 0
                                      ? selectedProducts.reduce((sum, pid) => {
                                          const product = products.find(p => p.id === pid);
                                          return sum + (product?.price || 0);
                                        }, 0).toFixed(2)
                                      : '0.00'
                              )}
                            </span>
                          </div>
                          
                          {/* Payment Status */}
                          <div className="mt-3 pt-3 border-t border-indigo-200">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Payment Status:</span>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                editingAppointment.payment_status === 'paid' 
                                  ? 'bg-green-100 text-green-800' 
                                  : editingAppointment.payment_status === 'partial'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {editingAppointment.payment_status === 'paid' ? '✓ Paid' : 
                                 editingAppointment.payment_status === 'partial' ? '◐ Partial' : 
                                 '○ Unpaid'}
                              </span>
                            </div>
                            
                            {editingAppointment.payment_status === 'partial' && editingAppointment.payment_amount && (
                              <p className="text-xs text-gray-500 mt-1">
                                Paid: ${editingAppointment.payment_amount.toFixed(2)} of ${editingAppointment.total_price?.toFixed(2) || '0.00'}
                              </p>
                            )}
                            
                            {editingAppointment.payment_date && (
                              <p className="text-xs text-gray-500 mt-1">
                                Last payment: {new Date(editingAppointment.payment_date).toLocaleDateString()}
                              </p>
                            )}
                            
                            {/* Quick Payment Actions */}
                            {editingAppointment.payment_status !== 'paid' && (
                              <div className="mt-3 flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Update payment status to paid
                                    setAppointmentForm({
                                      ...appointmentForm,
                                      payment_status: 'paid',
                                      payment_amount: editingAppointment.total_price || calculatedPricing?.total_price || 0,
                                      payment_date: new Date().toISOString()
                                    });
                                  }}
                                  className="flex-1 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  Mark as Paid
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const amount = prompt('Enter payment amount:');
                                    if (amount && !isNaN(parseFloat(amount))) {
                                      const totalPrice = editingAppointment.total_price || calculatedPricing?.total_price || 0;
                                      const paidAmount = parseFloat(amount);
                                      setAppointmentForm({
                                        ...appointmentForm,
                                        payment_status: paidAmount >= totalPrice ? 'paid' : 'partial',
                                        payment_amount: paidAmount,
                                        payment_date: new Date().toISOString()
                                      });
                                    }
                                  }}
                                  className="flex-1 px-3 py-2 bg-yellow-500 text-white text-xs font-medium rounded-lg hover:bg-yellow-600 transition-colors"
                                >
                                  Record Payment
                                </button>
                              </div>
                            )}
                            <StripePaymentSection
                              appointment={editingAppointment}
                              calculatedTotal={calculatedPricing?.total_price}
                              onRefunded={() => {
                                // simple full reload of appointment list
                                if (typeof window !== 'undefined') window.dispatchEvent(new Event('appointments-refresh'));
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Scheduling UI (new or rescheduling) */}
                    {(!editingAppointment || isReschedulingAppointment) && (
                      <>
                        {isReschedulingAppointment && (
                          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex justify-between items-center">
                            <span className="font-medium text-amber-800">Rescheduling Mode</span>
                            <button type="button" onClick={() => setIsReschedulingAppointment(false)} className="text-amber-600 hover:text-amber-800 text-sm">Cancel</button>
                          </div>
                        )}

                        {/* Photographer Selection */}
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Photographer</label>
                          <select
                            value={appointmentForm.photographer_id}
                            onChange={(e) => {
                              setAppointmentForm({...appointmentForm, photographer_id: e.target.value, scheduled_time: ''});
                              setAvailableDates({}); // Reset availability when photographer changes
                              if (e.target.value) {
                                // Fetch month availability for calendar
                                fetchMonthAvailability(e.target.value, calendarMonth);
                                if (appointmentForm.scheduled_date) {
                                  fetchPhotographerAvailability(e.target.value, appointmentForm.scheduled_date, calculatedPricing?.total_duration || 120);
                                }
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          >
                            <option value="">Select photographer</option>
                            <option value="any">Any Available</option>
                            {teamMembers.filter(member => {
                              if (selectedProducts.length === 0) return true;
                              const assignedProducts = member.assigned_products || [];
                              return selectedProducts.every(productId => assignedProducts.includes(productId));
                            }).map(member => (
                              <option key={member.id} value={member.id}>{member.name} - {member.role}</option>
                            ))}
                          </select>
                        </div>

                        {/* Manual Override */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" data-testid="manual-time-override-checkbox" checked={timeOverrideEnabled} onChange={(e) => setTimeOverrideEnabled(e.target.checked)} className="w-5 h-5 rounded border-amber-400 text-amber-600" />
                            <div>
                              <span className="font-medium text-amber-800">Manual Time Override</span>
                              <p className="text-xs text-amber-600">Bypass availability checks. Allows any date including past dates.</p>
                            </div>
                          </label>

                          {timeOverrideEnabled && (
                            <div className="mt-4 grid grid-cols-2 gap-3" data-testid="manual-override-datetime-fields">
                              <div>
                                <label className="block text-xs font-semibold text-amber-800 mb-1 uppercase tracking-wide">Date</label>
                                <input
                                  type="date"
                                  data-testid="manual-override-date-input"
                                  value={appointmentForm.scheduled_date || ''}
                                  onChange={(e) => setAppointmentForm({ ...appointmentForm, scheduled_date: e.target.value })}
                                  className="w-full px-3 py-2 border border-amber-300 rounded-lg bg-white text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-amber-800 mb-1 uppercase tracking-wide">Time</label>
                                <input
                                  type="time"
                                  data-testid="manual-override-time-input"
                                  value={appointmentForm.scheduled_time || ''}
                                  onChange={(e) => setAppointmentForm({ ...appointmentForm, scheduled_time: e.target.value })}
                                  className="w-full px-3 py-2 border border-amber-300 rounded-lg bg-white text-sm"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Date Selection - Calendar View */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Date *
                            {checkingAvailability && (
                              <span className="ml-2 inline-flex items-center text-blue-600">
                                <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-xs">Loading...</span>
                              </span>
                            )}
                          </label>
                          <div className="bg-white border border-gray-200 rounded-lg p-3 relative">
                            {/* Loading overlay for calendar */}
                            {checkingAvailability && (
                              <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
                                <div className="flex items-center gap-2 text-blue-600">
                                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span className="text-sm font-medium">Checking availability...</span>
                                </div>
                              </div>
                            )}
                            
                            <DayPicker
                              mode="single"
                              selected={appointmentForm.scheduled_date ? new Date(appointmentForm.scheduled_date + 'T12:00:00') : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  const dateStr = date.toISOString().split('T')[0];
                                  setAppointmentForm({...appointmentForm, scheduled_date: dateStr, scheduled_time: ''});
                                  setAvailableTimeSlots([]); // Clear old slots immediately
                                  if (appointmentForm.photographer_id && !timeOverrideEnabled) {
                                    fetchPhotographerAvailability(appointmentForm.photographer_id, dateStr, calculatedPricing?.total_duration || 120);
                                  }
                                  if (appointmentForm.photographer_id && appointmentForm.photographer_id !== 'any') {
                                    fetchPhotographerDayAppointments(appointmentForm.photographer_id, dateStr);
                                  }
                                }
                              }}
                              onMonthChange={(month) => {
                                setCalendarMonth(month);
                                setAvailableDates({}); // Clear old data
                                if (appointmentForm.photographer_id) {
                                  fetchMonthAvailability(appointmentForm.photographer_id, month);
                                }
                              }}
                              month={calendarMonth}
                              disabled={(date) => {
                                if (timeOverrideEnabled) return false;
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (date < today) return true;
                                const dateStr = date.toISOString().split('T')[0];
                                if (availableDates[dateStr] === false) return true;
                                return false;
                              }}
                              modifiers={{
                                available: (date) => {
                                  const dateStr = date.toISOString().split('T')[0];
                                  return availableDates[dateStr] === true;
                                },
                                unavailable: (date) => {
                                  const dateStr = date.toISOString().split('T')[0];
                                  return availableDates[dateStr] === false;
                                },
                                selected: (date) => {
                                  if (!appointmentForm.scheduled_date) return false;
                                  const dateStr = date.toISOString().split('T')[0];
                                  return dateStr === appointmentForm.scheduled_date;
                                }
                              }}
                              modifiersStyles={{
                                available: { backgroundColor: '#dcfce7', color: '#166534', fontWeight: '500' },
                                unavailable: { backgroundColor: '#fee2e2', color: '#991b1b', opacity: 0.4 },
                                selected: { backgroundColor: '#2563eb', color: 'white', fontWeight: '600', borderRadius: '8px' }
                              }}
                              modifiersClassNames={{
                                selected: 'ring-2 ring-blue-400 ring-offset-1'
                              }}
                              styles={{
                                caption: { color: '#1f2937', fontWeight: '600' },
                                head_cell: { color: '#6b7280', fontSize: '0.75rem', fontWeight: '500' },
                                day: { margin: '2px', borderRadius: '6px', transition: 'all 0.15s ease' },
                                nav_button: { color: '#3b82f6' },
                                nav_button_previous: { marginRight: '8px' },
                                nav_button_next: { marginLeft: '8px' }
                              }}
                              className="!font-sans"
                              fromDate={timeOverrideEnabled ? undefined : new Date()}
                            />
                            
                            {/* Legend and selected date info */}
                            <div className="mt-3 pt-3 border-t space-y-2">
                              {appointmentForm.scheduled_date && (
                                <div className="bg-blue-50 px-3 py-2 rounded-lg flex items-center justify-between">
                                  <span className="text-sm font-medium text-blue-800">
                                    Selected: {new Date(appointmentForm.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                  </span>
                                  <button 
                                    type="button"
                                    onClick={() => setAppointmentForm({...appointmentForm, scheduled_date: '', scheduled_time: ''})}
                                    className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                  >
                                    Clear
                                  </button>
                                </div>
                              )}
                              
                              {appointmentForm.photographer_id && (
                                <div className="flex items-center gap-4 text-xs text-gray-500 px-1">
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span> Available
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 rounded bg-red-100 border border-red-300 opacity-50"></span> Unavailable
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 rounded bg-blue-600"></span> Selected
                                  </span>
                                </div>
                              )}
                              
                              {!appointmentForm.photographer_id && (
                                <p className="text-xs text-amber-600 px-1">
                                  ⚠️ Select a photographer to see availability
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Time Slots - Show when date is selected */}
                        {appointmentForm.scheduled_date && (
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center justify-between">
                              <span>Available Times for {new Date(appointmentForm.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                              {checkingAvailability && (
                                <span className="inline-flex items-center text-blue-600">
                                  <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span className="text-xs">Loading slots...</span>
                                </span>
                              )}
                            </label>
                            {timeOverrideEnabled ? (
                              <input
                                type="time"
                                value={appointmentForm.scheduled_time}
                                onChange={(e) => setAppointmentForm({...appointmentForm, scheduled_time: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                              />
                            ) : checkingAvailability ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="flex flex-col items-center gap-2 text-gray-500">
                                  <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span className="text-sm">Finding available times...</span>
                                </div>
                              </div>
                            ) : availableTimeSlots.length > 0 ? (
                              <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                                {availableTimeSlots.map((slot) => (
                                  <button
                                    key={slot.time}
                                    type="button"
                                    onClick={() => setAppointmentForm({...appointmentForm, scheduled_time: slot.time})}
                                    className={`px-3 py-2.5 text-sm rounded-lg border transition-all duration-150 ${
                                      appointmentForm.scheduled_time === slot.time 
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105' 
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                                    }`}
                                  >
                                    {slot.time_12h}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-white rounded-lg border border-dashed border-gray-300">
                                <svg className="mx-auto h-10 w-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-gray-500">No available time slots for this date.</p>
                                <p className="text-xs text-gray-400 mt-1">Try a different date or enable manual override.</p>
                              </div>
                            )}
                            
                            {appointmentForm.scheduled_time && !timeOverrideEnabled && (
                              <div className="mt-3 pt-3 border-t flex items-center justify-between bg-green-50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                                <span className="text-sm font-medium text-green-800">
                                  ✓ Selected: {availableTimeSlots.find(s => s.time === appointmentForm.scheduled_time)?.time_12h || appointmentForm.scheduled_time}
                                </span>
                                <button 
                                  type="button"
                                  onClick={() => setAppointmentForm({...appointmentForm, scheduled_time: ''})}
                                  className="text-green-600 hover:text-green-800 text-xs font-medium"
                                >
                                  Change
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                      </>
                    )}
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex space-x-3 pt-6 border-t mt-6">
                  <button type="button" onClick={resetAppointmentForm} className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 font-medium" data-testid="appointment-form-cancel-btn">Cancel</button>
                  <button type="submit" className="flex-1 clara-btn-primary py-2 px-4" data-testid="appointment-form-submit-btn">
                    {editingAppointment ? 'Update Appointment' : 'Schedule Appointment'}
                  </button>
                </div>
              </form>
            </div>

            {/* Activity Drawer (slides in from right) */}
            {showActivity && editingAppointment && (
              <div className="fixed inset-0 z-[200]" data-testid="appointment-activity-drawer">
                <div className="absolute inset-0 bg-black/40" onClick={() => setShowActivity(false)} />
                <div className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col animate-slide-in-right">
                  <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Activity
                    </h3>
                    <button onClick={() => setShowActivity(false)} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="close-activity-drawer">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="p-5 overflow-y-auto flex-1">
                    <ActivityLog entityType="appointment" entityId={editingAppointment.id} />
                  </div>
                </div>
              </div>
            )}
          </div>
  );
};

export default AppointmentFormModal;
